import {objectFromEntries} from '@benchling/js-utils';

import {structuredTableActionOrigin} from 'analytics/Util';
import ArchiveModal, {ArchiveOptionsForModal} from 'archive/components/modals/ArchiveModal';
import {AnalyticsMetadataForContext} from 'constants/AnalyticsConstants';
import * as ApolloWrappers from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {ACTION_ARCHIVE_ROWS} from 'notebook/store/VersioningConstants';
import makeSimpleActionFunction from 'notebook/structured-tables/action-functions/makeSimpleActionFunction';
import {
  ActionResultType,
  performServerSideWriteAction,
} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import * as ActionFunctionDAO from 'notebook/structured-tables/ActionFunctionDAO';
import {
  OverflowMenuActionFunction,
  TableActionFunctionParameters,
  ToolbarButtonActionFunction,
} from 'notebook/structured-tables/StructuredTableConstants';
import legacyRunModal from 'ui-library/legacyRunModal';
import APIUtil from 'util/APIUtil';

import {StorableSummaryGraphQL} from '../../../../graphql-fragments/inventory/__generated/StorableSummaryGraphQL';
import {BioentitySummaryGraphQL} from '../../../../graphql-fragments/registry/registry-items/__generated/BioentitySummaryGraphQL';

export interface ItemToArchive {
  rowId: string;
  name: string;
  id: string;
}

export function runArchiveModal<TTableMetadata, TRowMetadata>({
  params,
  itemsFriendlyPlural,
  getItemsToArchive,
  analyticsMetadata,
  analyticsActionForPerfTracking,
}: {
  params: TableActionFunctionParameters<TTableMetadata, TRowMetadata>;
  itemsFriendlyPlural: string;
  getItemsToArchive: (
    params: TableActionFunctionParameters<TTableMetadata, TRowMetadata>
  ) => Array<ItemToArchive>;
  analyticsMetadata: AnalyticsMetadataForContext;
  analyticsActionForPerfTracking: string;
}): void {
  const itemsToArchive = getItemsToArchive(params);
  const rowForItem = objectFromEntries(itemsToArchive.map((item) => [item.id, item.rowId]));
  legacyRunModal(ArchiveModal, {
    items: itemsToArchive,
    showItemCheckboxes: true,
    analyticsMetadata: {
      ...analyticsMetadata,
      actionOrigin: analyticsMetadata.actionOrigin ?? structuredTableActionOrigin(params.structuredTable),
    },
    onArchive: (itemIds: Array<string>, purpose: string, options: ArchiveOptionsForModal) => {
      if (itemIds.length === 0) {
        // TODO(BNCH-41511) show error message instead
        return;
      }

      performServerSideWriteAction({
        entryId: params.entryOrTemplateId!,
        loadingScreenMessage: `Archiving ${itemsFriendlyPlural}...`,
        autoSaverAction: ACTION_ARCHIVE_ROWS,
        doPerformAction: async () => {
          const response = await ActionFunctionDAO.archiveRows(
            params.entryOrTemplateId!,
            params.structuredTable,
            params.computedTable,
            params.actionFunctionContext,
            itemIds.map((id) => ({
              id,
              modelType: APIUtil.apiIdToType(id),
              rowId: rowForItem[id],
            })),
            purpose,
            options
          );

          Object.entries(response.archiveRecordByApiId).forEach(([itemId, archiveRecord]) => {
            const fragmentName = APIUtil.isStorable(itemId)
              ? 'StorableSummary'
              : APIUtil.isBiologicalEntity(itemId)
                ? 'BioentitySummary'
                : null;

            if (fragmentName == null) {
              return;
            }

            // TODO(BNCH-41591): this would be cleaner using graphql_serialize_authorized_models
            const cachedFragment = ApolloWrappers.readFragmentFromCache(
              getFragmentWrapper(fragmentName),
              itemId
            );
            if (cachedFragment != null) {
              if (fragmentName === 'StorableSummary') {
                ApolloWrappers.writeFragmentToCache(getFragmentWrapper(fragmentName), {
                  ...cachedFragment,
                  archiveRecord,
                } as StorableSummaryGraphQL);
              } else {
                ApolloWrappers.writeFragmentToCache(getFragmentWrapper(fragmentName), {
                  ...cachedFragment,
                  archiveRecord:
                    archiveRecord != null ? {__typename: 'ArchiveRecordNode', ...archiveRecord} : null,
                } as BioentitySummaryGraphQL);
              }
            }
          });
          return {resultType: ActionResultType.SUCCESS, responseData: null};
        },
        analyticsActionForPerfTracking,
      });
    },
  });
}

export function makeArchiveItemsActionFunction<TTableMetadata, TRowMetadata>({
  itemsFriendlyPlural,
  getItemsToArchive,
  analyticsMetadata,
  analyticsActionForPerfTracking,
}: {
  itemsFriendlyPlural: string;
  getItemsToArchive: (
    params: TableActionFunctionParameters<TTableMetadata, TRowMetadata>
  ) => Array<ItemToArchive>;
  analyticsMetadata: AnalyticsMetadataForContext;
  analyticsActionForPerfTracking: string;
}): ToolbarButtonActionFunction<TTableMetadata, TRowMetadata> &
  OverflowMenuActionFunction<TTableMetadata, TRowMetadata> {
  return makeSimpleActionFunction<TTableMetadata, TRowMetadata>({
    label: `Archive ${itemsFriendlyPlural}`,
    icon: 'archive',
    getDisabled: (params) => params.locked || getItemsToArchive(params).length === 0,
    onInvoke: (params: TableActionFunctionParameters<TTableMetadata, TRowMetadata>) => {
      runArchiveModal({
        params,
        itemsFriendlyPlural,
        getItemsToArchive,
        analyticsMetadata,
        analyticsActionForPerfTracking,
      });
    },
  });
}
